/**
 * Profile Blog Tab Component
 * Displays discussions created by a specific user
 */

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material'
import {
  Forum as ForumIcon,
  ChatBubbleOutline as CommentIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { DiscussionDto } from '@api/types/social'
import { formatDistanceToNow } from 'date-fns'
import { CreateBlogPostDialog } from './CreateBlogPostDialog'
import { DiscussionThread } from '@features/social/components/DiscussionThread'

interface ProfileBlogTabProps {
  userId: string
  isOwnProfile?: boolean
}

// Sorting options
type SortOption = 'newest' | 'oldest' | 'mostReplies' | 'recentlyUpdated'

// Helper to safely format dates
function safeFormatDistanceToNow(dateString: string | undefined | null): string {
  if (!dateString) return 'recently'
  try {
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)
    const utcString = hasTimezone ? dateString : dateString + 'Z'
    const date = new Date(utcString)
    if (isNaN(date.getTime())) return 'recently'
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return 'recently'
  }
}

export function ProfileBlogTab({ userId, isOwnProfile = false }: ProfileBlogTabProps) {
  const navigate = useNavigate()
  const [sortBy, setSortBy] = useState<SortOption>('recentlyUpdated')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedDiscussionId, setSelectedDiscussionId] = useState<string | null>(null)

  // Fetch discussions for this user
  const {
    data: discussions,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['user-discussions', userId],
    queryFn: () => socialApi.getDiscussions(undefined, userId),
    enabled: !!userId,
    refetchOnWindowFocus: false,
  })

  // Sort discussions based on selected option
  const sortedDiscussions = useMemo(() => {
    if (!discussions) return []

    const sorted = [...discussions]

    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
      case 'mostReplies':
        return sorted.sort((a, b) => b.postCount - a.postCount)
      case 'recentlyUpdated':
        return sorted.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      default:
        return sorted
    }
  }, [discussions, sortBy])

  // Get the selected discussion object
  const selectedDiscussion = useMemo(() => {
    if (!selectedDiscussionId || !discussions) return null
    return discussions.find(d => d.id === selectedDiscussionId) || null
  }, [selectedDiscussionId, discussions])

  const handleDiscussionClick = (discussion: DiscussionDto) => {
    // For profile discussions, open them in the same view
    // For group discussions, navigate to the group page
    if (discussion.groupId) {
      navigate(`/groups/${discussion.groupId}?tab=discussions&discussionId=${discussion.id}`)
    } else {
      setSelectedDiscussionId(discussion.id)
    }
  }

  const handleBackToList = () => {
    setSelectedDiscussionId(null)
  }

  if (isLoading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    )
  }

  if (fetchError) {
    return (
      <Paper sx={{ p: 4 }}>
        <Alert severity="error">
          {fetchError instanceof Error ? fetchError.message : 'Failed to load blog posts'}
        </Alert>
      </Paper>
    )
  }

  // If a discussion is selected, show the discussion thread
  if (selectedDiscussionId && selectedDiscussion) {
    return (
      <DiscussionThread
        discussion={selectedDiscussion}
        onBack={handleBackToList}
        canReply={true}
        isModerator={false}
      />
    )
  }

  if (!discussions || discussions.length === 0) {
    return (
      <>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ForumIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Blog Posts Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {isOwnProfile ? "You haven't created any discussions yet" : "This user hasn't created any discussions yet"}
          </Typography>
          {isOwnProfile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              Create Post
            </Button>
          )}
        </Paper>
        {isOwnProfile && (
          <CreateBlogPostDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            userId={userId}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Paper sx={{ p: { xs: 1, sm: 3 } }}>
        {/* Header with sort and create button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Blog Posts ({discussions.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {isOwnProfile && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                size="small"
              >
                Create Post
              </Button>
            )}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as SortOption)}
              >
                <MenuItem value="recentlyUpdated">Recently Updated</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="mostReplies">Most Replies</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

      {/* Discussion List */}
      <List sx={{ p: 0 }}>
        {sortedDiscussions.map((discussion, index) => (
          <Box key={discussion.id}>
            {index > 0 && <Divider />}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleDiscussionClick(discussion)}
                sx={{
                  py: 2,
                  px: { xs: 1, sm: 2 },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {discussion.title}
                      </Typography>
                      {discussion.isPinned && (
                        <Chip label="Pinned" size="small" color="primary" />
                      )}
                      {discussion.isLocked && (
                        <Chip label="Locked" size="small" color="warning" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {/* Discussion preview text if available */}
                      {discussion.content && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {discussion.content}
                        </Typography>
                      )}
                      {/* Meta information */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CommentIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption" color="text.secondary">
                            {discussion.postCount} {discussion.postCount === 1 ? 'reply' : 'replies'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption" color="text.secondary">
                            Posted {safeFormatDistanceToNow(discussion.createdDate)}
                          </Typography>
                        </Box>
                        {discussion.groupName && (
                          <Chip
                            label={discussion.groupName}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            </ListItem>
          </Box>
        ))}
      </List>
    </Paper>
    {isOwnProfile && (
      <CreateBlogPostDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        userId={userId}
      />
    )}
    </>
  )
}
